/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { EmployeeModel } from '../../../../models/employee.model';
import { TaskModel } from '../../../../models/task.model';
import {
  BarChartDatasetsModel,
  BarChartModel,
  DistributionGraphModel
} from '../../../../models/timetracking.model';
import { WorkflowModel } from '../../../../models/workflow.model';
import { EmployeeService } from '../../../../services/employee.service';
import { TaskService } from '../../../../services/task.service';
import { TimeTrackingService } from '../../../../services/time-tracking.service';
import { WorkflowService } from '../../../../services/workflow.service';

@Component({
  selector: 'app-employee-dashboard',
  templateUrl: './employee-dashboard.component.html',
  styleUrls: ['./employee-dashboard.component.scss'],
  standalone: false
})
export class EmployeeDashboardComponent implements OnInit {
  @ViewChild(MatTable)
  table!: MatTable<object>;

  workflowChangedSubscription: Subscription = new Subscription();
  tasksChangedSubscription: Subscription = new Subscription();
  // page properties
  pageTitle: string = '';
  employeeId: string = '';
  employeeData!: EmployeeModel;
  workflowId: string = '';
  workflow!: WorkflowModel;
  tasks: TaskModel[] = [];
  loading: boolean = true;
  workflowTasks: TaskModel[] = [];
  // cards
  totalTime: number = 0;
  numberOfTasks: number = 0;
  numberOfCheckpoints: number = 0;
  precision: number = 5;
  // table
  displayedColumns: string[] = ['name', 'totalTime', 'medianTime'];

  //distribution chart properties
  subscription!: Subscription;
  taskId: string = ''; // ng model
  distributionGraphDataObject!: DistributionGraphModel;

  //Bar chart properties
  barChartDataObject!: BarChartModel;

  taskList: {
    name: string;
    timeTaken: number[];
    totalTime: number;
    medianTime: number;
  }[] = [];

  checkPointList: {
    name: string;
    timeTaken: number[];
    medianTime: number;
    totalTime: number;
  }[] = [];

  allCheckPoints: {
    name: string;
    timeTaken: number[];
    medianTime: number;
    totalTime: number;
  }[] = [];

  constructor(
    private route: ActivatedRoute,
    private workflowService: WorkflowService,
    private employeeService: EmployeeService,
    private taskService: TaskService,
    private timeTrackingService: TimeTrackingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(async (params) => {
      this.loading = true;
      this.allCheckPoints = [];
      this.checkPointList = [];
      this.taskList = [];
      this.workflowTasks = [];
      this.totalTime = 0;
      this.numberOfCheckpoints = 0;
      this.employeeId = params.employeeId;
      this.workflowId = params.workflowId;
      this.workflow = await this.workflowService.getWorkflow(this.workflowId);
      this.employeeData = await this.employeeService.getEmployee(this.employeeId);
      this.tasks = await this.getTasks(this.workflowId, params.fromDate, params.toDate);
      this.tasks.forEach((task: TaskModel) => {
        if (task.workflowId == this.workflowId) {
          if (!this.workflowTasks.includes(task)) {
            this.workflowTasks.push(task);
          }
        }
      });
      this.loading = false;
      if (this.workflow) {
        this.pageTitle = this.employeeData.name;
        let employeeTasks: TaskModel[] = this.workflowTasks.filter((task: TaskModel) =>
          task.executedBy.includes(this.employeeData.employeeId)
        );

        this.fillEmployeeCheckpoints(employeeTasks);

        this.fillAllEmployeesCheckpoints();

        for (let i = 0; i < this.taskList.length; i++) {
          this.taskList[i].medianTime = this.timeTrackingService.median(this.taskList[i].timeTaken);
        }
        for (let i = 0; i < this.allCheckPoints.length; i++) {
          this.allCheckPoints[i].medianTime = this.timeTrackingService.median(
            this.allCheckPoints[i].timeTaken
          );
        }

        //cards
        this.numberOfTasks = employeeTasks.length;

        //distribution chart
        this.distributionGraphDataObject = this.timeTrackingService.createDistributionGraph(
          this.taskList.map((x) => x.totalTime)
        );

        //Bar chart
        this.barChartDataObject = this.timeTrackingService.createBarChart(
          this.checkPointList.map((x) => x.name),
          this.getBarChartDatasets()
        );
      }
      this.cdr.detectChanges();
    });
  }
  getBarChartDatasets(): BarChartDatasetsModel[] {
    let barChartArray1 = this.checkPointList.map((x) => x.medianTime.toPrecision(2));
    let barChartArray2 = this.allCheckPoints.map((x) => x.medianTime.toPrecision(2));
    let barChartDatasets: BarChartDatasetsModel[] = [
      {
        label: $localize`:@@medianTimeForThisEmployee:Median Time For This Employee`,
        backgroundColor: '#4A7563',
        data: barChartArray1,
        maxBarThickness: 100
      },
      {
        label: $localize`:@@medianTimeForAllEmployees:Median Time For All Employees`,
        backgroundColor: '#d89849',
        data: barChartArray2,
        maxBarThickness: 100
      }
    ];
    return barChartDatasets;
  }
  ngOnDestroy() {
    this.workflowChangedSubscription.unsubscribe();
    this.tasksChangedSubscription.unsubscribe();
  }

  // distribution graph methods >>
  changeDistributionGraph(check: string) {
    if (check === '') {
      this.distributionGraphDataObject = this.timeTrackingService.createDistributionGraph(
        this.taskList.map((x) => x.totalTime)
      );
    } else {
      let obj = this.checkPointList.find((x) => x.name == check);
      let ind = this.checkPointList.indexOf(obj!);
      this.distributionGraphDataObject = this.timeTrackingService.createDistributionGraph(
        this.checkPointList[ind]?.timeTaken
      );
    }
  }
  fillEmployeeCheckpoints(employeeTasks: TaskModel[]) {
    for (let task of employeeTasks) {
      if (task.time_tracking && task.time_tracking.length >= 2) {
        let record: {
          name: string;
          timeTaken: number[];
          totalTime: number;
          medianTime: number;
        } = {
          name: task.name,
          timeTaken: [],
          totalTime: 0,
          medianTime: 0
        };
        let start: Date;
        let end: Date;
        for (let i = 0; i < task.time_tracking?.length!; i++) {
          let check = task.time_tracking![i];
          if (check.name === 'start') {
            start = check.start;
          } else if (check.name === 'end') {
            end = check.start;
          } else {
            let curr = check.start;
            let next = end!;
            if (i + 1 < task.time_tracking?.length!) {
              next = task.time_tracking![i + 1].start;
              let span = (Date.parse(next.toString()) - Date.parse(curr.toString())) / 3600000;
              if (!this.checkPointList.map((x) => x.name).includes(check.name)) {
                let point: {
                  name: string;
                  timeTaken: number[];
                  medianTime: number;
                  totalTime: number;
                } = {
                  name: check.name,
                  timeTaken: [],
                  medianTime: 0,
                  totalTime: 0
                };
                point.timeTaken.push(span);
                point.totalTime += span;
                this.checkPointList.push(point);
              } else {
                let checkpoint = this.checkPointList.find((x) => x.name === check.name);
                let checkInd = this.checkPointList.indexOf(checkpoint!);
                this.checkPointList[checkInd].timeTaken.push(span);
                this.checkPointList[checkInd].totalTime += span;
              }
              this.numberOfCheckpoints++;
            } else {
              end = check.start;
            }
          }
        }
        let diff = (Date.parse(end!.toString()) - Date.parse(start!.toString())) / 3600000;
        record.timeTaken.push(diff);
        record.totalTime += diff;
        this.taskList.push(record);
        this.totalTime += diff;
      }
    }
    this.table.renderRows();
  }
  async getTasks(workflowId: string, filterStart: string, filterEnd: string): Promise<TaskModel[]> {
    return (
      await this.taskService.selectTasks(
        {
          search_criteria: {
            status: { $eq: 'CLOSED' }
          },
          skip: 0,
          limit: 0,
          sort: 'name',
          select: 'task_id name assigned_to workflow_id executed_by claimed_by time_tracking'
        },
        filterStart,
        filterEnd
      )
    ).body;
  }
  fillAllEmployeesCheckpoints() {
    for (let i = 0; i < this.checkPointList.length; i++) {
      this.checkPointList[i].medianTime = this.timeTrackingService.median(
        this.checkPointList[i].timeTaken
      );
      let point: {
        name: string;
        timeTaken: number[];
        medianTime: number;
        totalTime: number;
      } = {
        name: this.checkPointList[i].name,
        timeTaken: [],
        medianTime: 0,
        totalTime: 0
      };
      this.allCheckPoints.push(point);
    }
    //Get time for all employees
    for (let task of this.workflowTasks) {
      if (task.time_tracking && task.time_tracking.length >= 2) {
        let start: Date;
        let end: Date;
        for (let i = 0; i < task.time_tracking?.length!; i++) {
          let check = task.time_tracking![i];
          if (check.name === 'start') {
            start = check.start;
          } else if (check.name === 'end') {
            end = check.start;
          } else {
            let curr = check.start;
            let next = end!;
            let span;
            if (i + 1 < task.time_tracking?.length!) {
              next = task.time_tracking![i + 1].start;
              span = (Date.parse(next.toString()) - Date.parse(curr.toString())) / 3600000;
              if (this.allCheckPoints.map((x) => x.name).includes(check.name)) {
                let obj = this.allCheckPoints.find((x) => x.name == check.name);
                let ind = this.allCheckPoints.indexOf(obj!);
                this.allCheckPoints[ind].timeTaken.push(span);
                this.allCheckPoints[ind].totalTime += span;
              }
            } else {
              end = check.start;
            }
          }
        }
        if (
          this.taskList.map((x) => x.name).includes(task.name) &&
          !task.executedBy.includes(this.employeeId)
        ) {
          let diff = (Date.parse(end!.toString()) - Date.parse(start!.toString())) / 3600000;
          let obj = this.taskList.find((x) => x.name == task.name);
          let ind = this.taskList.indexOf(obj!);
          this.taskList[ind].timeTaken.push(diff);
          this.taskList[ind].totalTime += diff;
        }
      }
    }
  }
}
